import React, { useState, useCallback } from 'react'

import { isValidCssValue } from '../../lib/cssValue'

/**
 * Length control for the theme tokens. Stores a CSS length STRING
 * (`'6px'`), not a number.
 *
 * That distinction is load-bearing: the player writes the stored value
 * straight into a custom property via `setProperty('--player-radius', v)`.
 * A bare `6` is not a valid CSS length, so the declaration would be
 * dropped and the control would appear to do nothing. NumberSpinner --
 * the existing numeric input -- stores a raw number, which is why this
 * is a separate component rather than a reuse.
 *
 * '' means unset: the slider rests at the player's own default and the
 * stored value stays empty, so fill-only fan-out still sees the
 * preference as unthemed.
 *
 * TWO THINGS A PLAIN SLIDER GOT WRONG
 *
 * 1. It could not round-trip anything but a plain length. These tokens are
 *    custom properties, so they accept arbitrary CSS, and clients use it:
 *    CVTV's production title size is
 *    `clamp(1.5rem, .25rem + 2.67vw, 2.25rem)` -- fluid type set through the
 *    API. A slider reading that has no representation for it and wrote back a
 *    fixed length on the next save, destroying a value the editor never
 *    showed the user in the first place. Silent coercion is the one outcome
 *    ruled out, so a value the slider cannot represent is now displayed as
 *    itself, read-only, and replacing it takes a deliberate click.
 *
 * 2. It only emitted its own unit. The text escape hatch accepts any CSS
 *    length -- but note that the SLIDER always emits `px` on purpose: the
 *    player's postcss transform rewrites `rem` in its compiled stylesheet so
 *    the player is immune to a host page's `html { font-size: 62.5% }`, and
 *    it cannot touch a value that arrives as an inline custom property. A
 *    stored `0.5rem` is 8px on a normal site and 5px on a 62.5% root; a
 *    stored `8px` is 8px everywhere.
 *
 * @param {object} props
 * @param {string} props.value            stored length, e.g. '6px'; '' when unset
 * @param {string} props.fallback         player's own default, e.g. '6px'
 * @param {number} props.min
 * @param {number} props.max
 * @param {number} props.step
 * @param {string} [props.unit]      CSS unit the slider emits; defaults to 'px'.
 *                                  Hover strength is a percentage, so the unit
 *                                  cannot be assumed.
 * @param {string} props.preferenceName
 * @param {string} [props.cssProperty]  real CSS property to screen text against
 * @param {string} [props.cssFormat]    'percentage' for the color-mix values
 * @param {(value: string) => void} props.storeEdits
 */

function toNumber (length, fallback) {
  const parsed = parseFloat(length)
  if (!isNaN(parsed)) return parsed
  const parsedFallback = parseFloat(fallback)
  return isNaN(parsedFallback) ? 0 : parsedFallback
}

/**
 * Can the slider represent this value without losing anything?
 *
 * Only a plain number in the slider's own unit. Everything else -- `clamp()`,
 * `calc()`, a `rem` or `vw` length, a keyword -- round-trips through the
 * slider as a different value than it went in as, so it is held read-only
 * rather than quietly rewritten.
 */
export function isPlainLength (value, unit) {
  if (typeof value !== 'string' || value === '') return true
  const escaped = (unit || 'px').replace('%', '\\%')
  return new RegExp('^\\s*-?\\d+(\\.\\d+)?' + escaped + '\\s*$').test(value)
}

const LengthSlider = props => {
  const {
    value, fallback, min = 0, max = 16, step = 1, unit = 'px',
    preferenceName, storeEdits, cssProperty, cssFormat
  } = props

  // Two escape hatches from the slider, both explicit:
  //   `override` -- the stored value is something the slider cannot express
  //   and the user has asked to replace it anyway.
  //   `asText` -- the user wants to type a value the slider cannot produce.
  const [override, setOverride] = useState(false)
  const [asText, setAsText] = useState(false)
  const [draft, setDraft] = useState(value || '')

  const isSet = typeof value === 'string' && value !== ''
  const current = toNumber(value, fallback)
  const representable = isPlainLength(value, unit)

  const handleChange = useCallback(ev => {
    storeEdits(ev.target.value + unit)
  }, [storeEdits, unit])

  const handleText = useCallback(ev => {
    setDraft(ev.target.value)
  }, [])

  // Committed on blur rather than per keystroke: a half-typed `clamp(` is not
  // a length, and pushing it live would blank the token in the preview on the
  // way to a valid value.
  //
  // SCREENED. An unparseable length is dropped by `setProperty()` while still
  // reading as non-empty, so it would leave a preference that paints nothing
  // AND is excluded from fill-only fan-out. Delegating to the CSS parser keeps
  // `clamp()`, `calc()` and `min()` -- which is the entire reason this field
  // exists -- while refusing what the browser would throw away.
  const [rejected, setRejected] = useState(false)
  const handleTextCommit = useCallback(() => {
    const next = draft.trim()
    if (!isValidCssValue(cssProperty || 'width', next, cssFormat)) {
      setRejected(true)
      return
    }
    setRejected(false)
    if (next !== value) storeEdits(next)
  }, [draft, value, storeEdits, cssProperty, cssFormat])

  // A value the slider cannot represent, shown as itself. No slider is
  // rendered at all until the user says to replace it, so there is no
  // control sitting at a position that misrepresents what is stored.
  if (isSet && !representable && !override) {
    return (
      <div className="theme-length theme-length--external">
        <code className="theme-length__external-value" title={value}>{value}</code>
        <p className="theme-length__external-note">
            Set outside the editor. The slider cannot show this value without
            changing it.
        </p>
        <button
          type="button"
          className="theme-length__external-replace"
          onClick={() => { setDraft(value); setOverride(true) }}
        >
            Replace it
        </button>
      </div>
    )
  }

  if (asText) {
    return (
      <div className={'theme-length theme-length--text' + (rejected ? ' theme-length--rejected' : '')}>
        <input
          type="text"
          className="form-control theme-length__text"
          name={preferenceName}
          value={draft}
          placeholder={fallback || ''}
          aria-invalid={rejected}
          onChange={handleText}
          onBlur={handleTextCommit}
        />
        <button
          type="button"
          className="theme-length__mode"
          onClick={() => setAsText(false)}
          title="Back to the slider"
        >
          <i className="fa fa-sliders" aria-hidden="true"/>
        </button>
        {/* Says what happened rather than silently reverting. The value stays
            in the field so it can be corrected instead of retyped. */}
        {rejected ? (
          <p className="theme-length__rejected-note" role="alert">
            Not a value the player can use. It would be discarded and the
            control would appear to do nothing.
          </p>
        ) : null}
      </div>
    )
  }

  return (
    <div className="theme-length theme-length-slider">
      <input
        type="range"
        name={preferenceName}
        min={min}
        max={max}
        step={step}
        value={current}
        onChange={handleChange}
      />
      <span className={'theme-length-slider__value' + (isSet ? '' : ' theme-length-slider__value--default')}>
        {isSet ? value : String(current) + unit}
      </span>
      {/* Same tag the colour control carries, for the same reason: a slider
          resting at the player's own default is indistinguishable from one
          somebody deliberately set there. */}
      {!isSet ? <span className="theme-color__tag">default</span> : null}
      {/* The escape hatch, always available rather than only after the
          editor meets a value it cannot handle -- fluid type is a thing a
          client may want to SET here, not only something they arrive with. */}
      <button
        type="button"
        className="theme-length__mode"
        onClick={() => { setDraft(value || ''); setAsText(true) }}
        title="Enter any CSS length"
      >
        <i className="fa fa-pencil" aria-hidden="true"/>
      </button>
    </div>
  )
}

export default LengthSlider
